/**
 * CharacterTile.js
 * - Preserves existing behavior (overlay enlarge, click-outside to close, image regenerate, toolbar).
 * - NEW: Section-driven, in-place editor for the LEFT column that mirrors display logic.
 *   It parses character.details.description into ordered sections (e.g., "role:", "arc:", "goals:", etc.),
 *   lets the user edit BOTH the section names and their content, add/remove sections,
 *   and then rebuilds description text on Save, preserving order and any unknown sections.
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import useClickOutside from './hooks/useClickOutside';
import './CharacterTile.css';

import { saveImageOnCloudB64 } from '../services/db';
import { ImageAPI } from '../services/backend/imageService.backend';

/* ─────────────────────────────────────────────────────────
   Display helper (unchanged): render description into blocks
   ───────────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────────
   Display helper: render description into elegant blocks
   ───────────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────────
   Display helper: render description into elegant blocks with grouping
   ───────────────────────────────────────────────────────── */
const renderElegantDescription = (description) => {
  const { preamble, sections } = parseDescriptionToSections(description || '');

  // Heuristic: These keys ALWAYS need full width.
  const FULL_WIDTH_KEYS = new Set(['BIO', 'ROLE', 'ARC', 'GOALS', 'BACKGROUND', 'PERSONALITY', 'NOTES', 'SUMMARY']);

  // Heuristic: Max length for a value to be considered a "stat" for the grid.
  const MAX_GRID_VALUE_LENGTH = 100;

  // Grouping pass
  const grouped = [];
  let buffer = [];

  const flushBuffer = () => {
    if (buffer.length > 0) {
      grouped.push({ type: 'grid', items: buffer });
      buffer = [];
    }
  };

  sections.forEach(sec => {
    const keyUpper = (sec.key || '').toUpperCase();
    const value = sec.lines.join(' '); // Check flat length

    // It goes in the grid if:
    // 1. It's NOT a forced full-width key
    // 2. AND the content is short enough to fit in a column
    const isGridItem = !FULL_WIDTH_KEYS.has(keyUpper) && value.length <= MAX_GRID_VALUE_LENGTH;

    if (isGridItem) {
      buffer.push(sec);
    } else {
      flushBuffer();
      grouped.push({ type: 'block', item: sec });
    }
  });
  flushBuffer();

  return (
    <>
      {preamble.length > 0 && (
        <div className="nw-info-block compact">
          <div className="nw-value pre-wrap">{preamble.join('\n')}</div>
        </div>
      )}
      {grouped.map((group, i) => {
        if (group.type === 'grid') {
          return (
            <div className="nw-stats-grid" key={i}>
              {group.items.map((sec, j) => (
                <div key={j}>
                  <div className="nw-label">{sec.key}</div>
                  <div className="nw-value">{sec.lines.join(', ')}</div>
                </div>
              ))}
            </div>
          );
        } else {
          const { item } = group;
          return (
            <div className="nw-info-block compact" key={i}>
              <div className="nw-label">{item.key}</div>
              <div className="nw-value pre-wrap">{item.lines.join('\n')}</div>
            </div>
          );
        }
      })}
    </>
  );
};

/* ─────────────────────────────────────────────────────────
   Section parser/builder that mirrors how we display
   - Supports multi-line sections until next "Key:" line
   - Preserves order, unknown keys, and preamble lines
   ───────────────────────────────────────────────────────── */
function parseDescriptionToSections(desc = '') {
  const lines = desc.split('\n');
  const preamble = [];
  const sections = [];
  let current = null;

  for (const raw of lines) {
    const line = raw.replace(/\r$/, ''); // normalize CRLF
    const m = line.match(/^([^\s:][^:]*?)\s*:\s*(.*)$/); // "Key:" then optional inline first line
    if (m) {
      // start a new section
      const key = m[1].trim();
      const first = m[2] ?? '';
      current = { key, lines: [] };
      if (first.length) current.lines.push(first);
      sections.push(current);
    } else if (current) {
      current.lines.push(line);
    } else {
      preamble.push(line);
    }
  }

  // trim trailing blanks in each section
  sections.forEach(s => {
    while (s.lines.length && s.lines[s.lines.length - 1].trim() === '') s.lines.pop();
  });

  // also trim preamble bottom blanks
  while (preamble.length && preamble[preamble.length - 1].trim() === '') preamble.pop();

  return { preamble, sections };
}

function buildDescriptionFromSections({ preamble, sections }) {
  const out = [];
  if (preamble && preamble.length) out.push(...preamble);

  for (const s of sections) {
    // emit heading
    if (!s || !s.key) continue;
    const key = s.key.trim();
    const lines = Array.isArray(s.lines) ? s.lines : [];
    if (lines.length === 0) {
      out.push(`${key}:`);
      continue;
    }
    // first line inline, rest on new lines
    out.push(`${key}: ${lines[0]}`);
    for (let i = 1; i < lines.length; i++) out.push(lines[i]);
  }

  return out.join('\n');
}

/* ─────────────────────────────────────────────────────────
   Tiny inline editors
   ───────────────────────────────────────────────────────── */
function SectionEditorRow({ section, onChangeKey, onChangeLines, onRemove }) {
  const value = (section.lines || []).join('\n');
  return (
    <div className="character-section" style={{ marginBottom: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <input
          className="sp-input"
          value={section.key}
          onChange={(e) => onChangeKey(e.target.value)}
          placeholder="Section name (e.g., role)"
        />
        <button className="sp-btn" onClick={onRemove} title="Remove section">×</button>
      </div>
      <textarea
        className="sp-textarea"
        rows={4}
        value={value}
        onChange={(e) => onChangeLines(e.target.value.split('\n'))}
        placeholder="Content (multiple lines allowed)"
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Component
   ───────────────────────────────────────────────────────── */
const CharacterTile = ({ character, setIsOverlayVisible, onSaveCharacter, onRegenerateCharacterFull, characterIsRegenerating = {}, imageModels = [], projectId, onValidateContent }) => {
  const [isEnlarged, setIsEnlarged] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // {x, y}

  // Full regeneration loading state for this specific character
  const isRegeneratingFull = !!characterIsRegenerating[character.name];

  // Keep legacy description flow so nothing breaks upstream
  const [description, setDescription] = useState(character.details.description);

  // Image regeneration states (existing feature)
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [imageUrl, setImageUrl] = useState(character.details.image);

  // Section draft for left-column editing (preamble + ordered sections)
  const [preambleDraft, setPreambleDraft] = useState([]);
  const [sectionsDraft, setSectionsDraft] = useState([]); // [{key, lines:[]}, ...]

  // Validation state
  const [validationError, setValidationError] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  const tileRef = useRef(null);

  useEffect(() => {
    setDescription(character.details.description);
    setImageUrl(character.details.image);

    // Reset drafts from current description so editor always reflects display
    const parsed = parseDescriptionToSections(character.details.description || '');
    setPreambleDraft(parsed.preamble);
    setSectionsDraft(parsed.sections);
  }, [character]);

  const toggleEnlarge = (e) => {
    if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON' || e.target.closest('button')) return;
    setIsEnlarged(true);
    setIsOverlayVisible(true);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
    // (drafts already seeded in useEffect from current description)
  };

  const handleSaveClick = async (e) => {
    e.stopPropagation();
    setValidationError(null);

    // Rebuild description from *edited* dynamic sections
    const rebuilt = buildDescriptionFromSections({
      preamble: preambleDraft,
      sections: sectionsDraft.filter(s => s && String(s.key || '').trim().length), // drop empty keys
    });

    // Validation Check
    if (onValidateContent) {
      setIsValidating(true);
      const check = await onValidateContent(rebuilt);
      setIsValidating(false);

      if (check && !check.valid) {
        setValidationError(check.reason);
        return;
      }
    }

    const updated = {
      ...character,
      details: {
        ...character.details,
        description: rebuilt,
        image: imageUrl // keep any regenerated image
      },
    };

    onSaveCharacter(updated);

    setIsEditing(false);
  };

  const handleCancelClick = (e) => {
    e.stopPropagation();
    setValidationError(null);

    // Reset drafts to original character data
    const parsed = parseDescriptionToSections(character.details.description || '');
    setPreambleDraft(parsed.preamble);
    setSectionsDraft(parsed.sections);

    setIsEditing(false);
  };

  const handleCharacterDescriptionChange = (e) => setDescription(e.target.value);

  /* ─────────────────────────────────────────────────────────
     Handlers
     ───────────────────────────────────────────────────────── */
  // Cleanup and reset if tile minimizes
  useEffect(() => {
    if (!isEnlarged) {
      setContextMenu(null);
      setFullRegenContextMenu(null);
      setValidationError(null); // Clear validation error

      // Reset drafts to original character data (Cancel edits)
      const parsed = parseDescriptionToSections(character.details.description || '');
      setPreambleDraft(parsed.preamble);
      setSectionsDraft(parsed.sections);
    }
  }, [isEnlarged, character.details.description]);

  const handleClickOutside = (e) => {
    // If a context menu is open, let its own backdrop handle the close logic.
    // Don't minimize the tile.
    if (contextMenu || fullRegenContextMenu) return;

    if (tileRef.current && !tileRef.current.contains(e.target)) {
      setIsEnlarged(false);
      setIsEditing(false);
      setIsOverlayVisible(false);
    }
  };

  /* ─────────────────────────────────────────────────────────
     Handlers
     ───────────────────────────────────────────────────────── */
  const handleRegenerateClick = async (eOrModelOverride) => {
    // If it's an event object, stop propagation. 
    // If it's a string (model alias), use it as override.
    let modelOverride = null;
    if (eOrModelOverride && eOrModelOverride.stopPropagation) {
      eOrModelOverride.stopPropagation();
    } else if (typeof eOrModelOverride === 'string') {
      modelOverride = eOrModelOverride;
    }

    if (isRegenerating) return;
    try {
      setIsRegenerating(true);
      const prompt = description || character.details.description;
      // Pass model override if present
      const b64 = await ImageAPI.character({ character: prompt, model: modelOverride || undefined });
      const url = await saveImageOnCloudB64(b64, `character_${character.name}_${Date.now()}`, projectId, 'characters');
      setImageUrl(url);

      const updated = { ...character, details: { ...character.details, image: url, description: prompt } };
      onSaveCharacter(updated);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Regenerate failed:', err);
      alert('Failed to regenerate character image.');
    } finally {
      setIsRegenerating(false);
      setContextMenu(null); // Close menu if open
    }
  };

  useClickOutside(tileRef, handleClickOutside, isEnlarged);

  /* LEFT column renderer: read-only vs dynamic section editor */
  const renderLeftColumn = () => {
    if (!isEditing) {
      return <div className="character-details scrollable">{renderElegantDescription(character.details.description)}</div>;
    }

    return (
      <div className="character-details scrollable" onClick={(e) => e.stopPropagation()}>
        {/* Optional preamble editor if you keep intro lines above first section */}
        {preambleDraft.length > 0 && (
          <div className="character-section" style={{ marginBottom: 12 }}>
            <div className="field-label" style={{ marginBottom: 4 }}>Intro (preamble)</div>
            <textarea
              className="sp-textarea"
              rows={3}
              value={preambleDraft.join('\n')}
              onChange={(e) => setPreambleDraft(e.target.value.split('\n'))}
            />
          </div>
        )}

        {/* Dynamic sections */}
        {(sectionsDraft || []).map((sec, idx) => (
          <SectionEditorRow
            key={idx}
            section={sec}
            onChangeKey={(k) =>
              setSectionsDraft((list) => {
                const next = list.slice();
                next[idx] = { ...next[idx], key: k };
                return next;
              })
            }
            onChangeLines={(lns) =>
              setSectionsDraft((list) => {
                const next = list.slice();
                next[idx] = { ...next[idx], lines: lns };
                return next;
              })
            }
            onRemove={() =>
              setSectionsDraft((list) => list.filter((_, i) => i !== idx))
            }
          />
        ))}

        <div className="sp-editor-actions" style={{ marginTop: 8 }}>
          <button
            className="sp-btn"
            onClick={() => setSectionsDraft((list) => [...(list || []), { key: 'new_section', lines: [''] }])}
          >
            + Add Section
          </button>
        </div>

        {/* Keep legacy full description textarea for compatibility (hidden while editing by default).
            If you want to keep it visible, uncomment below. */}
        {/* <div className="character-section" style={{ marginTop: 12 }}>
          <div className="field-label">Raw Description (legacy)</div>
          <textarea className="sp-textarea" rows={4} value={description} onChange={handleCharacterDescriptionChange} />
        </div> */}
      </div>
    );
  };

  // State for Full Regeneration Context Menu (Instructions)
  const [fullRegenContextMenu, setFullRegenContextMenu] = useState(null); // {x, y}
  const [regenInstructions, setRegenInstructions] = useState("");

  const handleFullRegenRightClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setFullRegenContextMenu({ x: rect.left, y: rect.bottom });
    setRegenInstructions(""); // reset last instructions
  };

  const submitFullRegen = () => {
    onRegenerateCharacterFull?.(character.name, regenInstructions);
    setFullRegenContextMenu(null);
  };

  /* ─────────────────────────────────────────────────────────
     Render Logic: Portal vs Inline
     ───────────────────────────────────────────────────────── */
  const tileContent = (
    <div className={`character-tile ${isEnlarged ? 'enlarged' : ''}`} onClick={toggleEnlarge} ref={tileRef}>
      {/* Tile Button: Full Regeneration (Details + Image) */}
      {!isEnlarged && (
        <button
          className={`regen-mini-btn ${isRegeneratingFull ? 'regen-active' : ''}`}
          onClick={(e) => { e.stopPropagation(); onRegenerateCharacterFull?.(character.name); }}
          onContextMenu={handleFullRegenRightClick}
          title="Regenerate Full Character (Right-click for options)"
          aria-label="Regenerate Full Character"
          disabled={isRegeneratingFull}
        >

          <span className={isRegeneratingFull ? 'rotating' : ''}>
            {isRegeneratingFull ? '↻' : '↻'}
          </span>
        </button>
      )}

      {/* Tile Button: Image Regeneration Only */}
      {!isEnlarged && (
        <button
          className={`regen-image-mini-btn ${isRegenerating ? 'regen-active' : ''}`}
          onClick={handleRegenerateClick}
          onContextMenu={(e) => {
            e.preventDefault();
            if (imageModels && imageModels.length > 0) {
              setContextMenu({ x: e.clientX, y: e.clientY });
            }
          }}
          title="Regenerate Image Only (Right-click for options)"
          aria-label="Regenerate Image"
          disabled={isRegenerating}
        >
          {isRegenerating ? (
            <span className="spin-icon">↻</span>
          ) : (
            <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" fill="currentColor" fillOpacity="0.5" />
              <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="16.5" cy="16.5" r="7" fill="#111" stroke="currentColor" strokeWidth="1.5" />
              <g transform="translate(16.5 16.5) scale(1.6) translate(-18.5 -18.5)">
                <path d="M19.7 17.3C19.7 17.3 19.5 16.8 18.5 16.8C17.5 16.8 16.7 17.6 16.7 18.6C16.7 19.6 17.5 20.4 18.5 20.4C19.2 20.4 19.8 20 20.1 19.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                <path d="M19.7 16.8V17.5H19" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </g>
            </svg>
          )}
        </button>
      )}

      {/* Tile Button: Edit (Mini) */}
      {!isEnlarged && (
        <button
          className="edit-mini-btn"
          onClick={(e) => {
            e.stopPropagation();
            setIsEnlarged(true);
            setIsOverlayVisible(true);
            setIsEditing(true);
          }}
          title="Edit Character"
          aria-label="Edit Character"
        >
          ✎
        </button>
      )}

      {/* Tile Button: Edit (Mini) */}
      {!isEnlarged && (
        <button
          className="edit-mini-btn"
          onClick={(e) => {
            e.stopPropagation();
            setIsEnlarged(true);
            setIsOverlayVisible(true);
            setIsEditing(true);
          }}
          title="Edit Character"
          aria-label="Edit Character"
        >
          ✎
        </button>
      )}

      <div className={`character-header ${isRegeneratingFull ? 'tile-blur' : ''}`}>
        <div className="character-info">
          <h1>{character.name}</h1>
          {renderLeftColumn()}
        </div>

        {/* RIGHT: image preview */}
        <div className="character-image-wrapper">
          <img
            src={imageUrl}
            alt={character.name}
            className={`character-image${isRegenerating ? ' blur-effect' : ''}`}
          />

          {/* Validation Error Overlay */}


          {isEnlarged && (
            <div className="vertical-buttons-screenplay-container">
              <button
                className={`toolbar-button ${isRegenerating ? 'regen-active' : ''}`}
                onClick={handleRegenerateClick}
                onContextMenu={(e) => {
                  e.preventDefault();
                  if (imageModels && imageModels.length > 0) {
                    setContextMenu({ x: e.clientX, y: e.clientY });
                  }
                }}
                title="Regenerate Image Only (Right-click for options)"
                aria-label="Regenerate Image"
                disabled={isRegenerating}
              >
                {isRegenerating ? (
                  <span className="spin-icon">↻</span>
                ) : (
                  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" fill="currentColor" fillOpacity="0.5" />
                    <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="16.5" cy="16.5" r="7" fill="#111" stroke="currentColor" strokeWidth="1.5" />
                    <g transform="translate(16.5 16.5) scale(1.6) translate(-18.5 -18.5)">
                      <path d="M19.7 17.3C19.7 17.3 19.5 16.8 18.5 16.8C17.5 16.8 16.7 17.6 16.7 18.6C16.7 19.6 17.5 20.4 18.5 20.4C19.2 20.4 19.8 20 20.1 19.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                      <path d="M19.7 16.8V17.5H19" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                  </svg>
                )}
              </button>

              <button className="toolbar-button" onClick={e => { e.stopPropagation(); setIsEditing(true); }} aria-label="Edit">
                ✎
              </button>

              <button
                className="toolbar-button"
                onClick={(e) => { e.stopPropagation(); onRegenerateCharacterFull?.(character.name); }}
                onContextMenu={handleFullRegenRightClick}
                aria-label="Regenerate Full Character"
                disabled={isRegeneratingFull}
                title="Regenerate Full Character (Right-click for options)"
              >
                <span className={isRegeneratingFull ? 'rotating' : ''}>
                  {isRegeneratingFull ? '↻' : '↻'}
                </span>
              </button>

              {isEditing && (
                <>
                  <button
                    className="toolbar-button"
                    onClick={handleCancelClick}
                    aria-label="Cancel"
                    title="Cancel Changes"
                  >
                    ✕
                  </button>
                  <button
                    className="toolbar-button"
                    onClick={handleSaveClick}
                    aria-label="Save"
                    disabled={isValidating}
                    title="Save Changes"
                  >
                    {isValidating ? "..." : "💾"}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Validation Error Overlay (Below Buttons) */}
          {isEnlarged && isEditing && validationError && (
            <div style={{
              marginTop: '12px',
              background: 'rgba(40, 0, 0, 0.9)',
              border: '1px solid #8B0000',
              borderLeft: '4px solid #ff4444',
              color: '#ffcccc',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '0.8rem',
              width: '100%',
              boxSizing: 'border-box',
              boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
              backdropFilter: 'blur(4px)',
              textAlign: 'left'
            }}>
              <strong>⚠ Policy Violation:</strong> {validationError}
            </div>
          )}
        </div>
      </div>



      {/* Context Menu for Models - Portaled to body */}
      {contextMenu && createPortal(
        <>
          <div className="character-tile-context-backdrop" onClick={(e) => { e.stopPropagation(); setContextMenu(null); }} />
          <div
            className="character-tile-context-menu"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="context-menu-header">Select Model</div>
            {imageModels.map(m => (
              <div
                key={m.alias}
                className="context-menu-item"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRegenerateClick(m.alias);
                }}
              >
                {m.label || m.alias}
              </div>
            ))}
          </div>
        </>,
        document.body
      )}

      {/* Instruction Popup for Full Regen - Portaled to body */}
      {fullRegenContextMenu && createPortal(
        <>
          <div className="character-tile-context-backdrop" onClick={(e) => { e.stopPropagation(); setFullRegenContextMenu(null); }} />
          <div
            className="character-tile-instruction-popup"
            style={{ top: fullRegenContextMenu.y + 5, left: fullRegenContextMenu.x }} // Small gap below button
            onClick={(e) => e.stopPropagation()}
          >
            <textarea
              className="instruction-popup-input"
              placeholder="Instructions (e.g. 'Short hair'). Press Enter to regenerate."
              value={regenInstructions}
              onChange={(e) => setRegenInstructions(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault(); // create new line only with shift
                  submitFullRegen();
                }
              }}
            />
          </div>
        </>,
        document.body
      )}
    </div>
  );

  if (isEnlarged) {
    return (
      <>
        {/* Placeholder: Keeps grid layout stable while real tile is in portal */}
        <div className="character-tile" style={{ opacity: 0, pointerEvents: 'none', visibility: 'hidden' }}>
          {/* Mimic basic structure for size */}
          <div className="character-header">
            <div className="character-info">
              <h1>{character.name}</h1>
              <div className="character-details scrollable">{renderElegantDescription(character.details.description)}</div>
            </div>
            <div className="character-image-wrapper">
              <img src={imageUrl} alt={character.name} className="character-image" />
            </div>
          </div>
        </div>

        {createPortal(
          <>
            <div
              className="character-tile-backdrop"
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9990 }}
              onClick={(e) => {
                e.stopPropagation();
                setIsEnlarged(false);
                setIsEditing(false);
                setIsOverlayVisible(false);
              }}
            />
            {tileContent}
          </>,
          document.body
        )}
      </>
    );
  }

  return tileContent;
};

export default CharacterTile;
